/* eslint-disable import/no-named-as-default */
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { ZodSchema } from "zod";

import StepIndicator from "@/app/used-car-finance/components/StepIndicator";
import Button from "@/components/ui/buttons/Button";
import { HeadingTag } from "@/utils/constants";

interface Step {
  label: string;
  index: number;
}

export interface StepData {
  component: React.ComponentType<any>;
  onStepSubmit?: (data: any, allFormData?: any) => Promise<void>;
  step?: Step;
  submitStep?: boolean;
  usesCustomNextButton?: boolean;
  isSuccessStep?: boolean;
}

interface MultistepFormProps {
  stepsData: StepData[];
  onSubmit: (data: any) => Promise<void>;
  stepTitles: string[];
  title: string;
  description: string;
  schemas: { [key: number]: ZodSchema };
  TitleTag?: HeadingTag;
  DescriptionTag?: React.ElementType;
}

// Helper function to check if any uploads are in progress
const hasUploadsInProgress = (data: any): boolean => {
  if (!data) return false;

  // Check all form fields recursively for uploads in progress
  const checkValue = (value: any): boolean => {
    // Check arrays (like image arrays from ImageUploader)
    if (Array.isArray(value)) {
      return value.some((item) => item && typeof item === "object" && item.status === "uploading");
    }

    // Check objects recursively
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return Object.values(value).some((v) => checkValue(v));
    }

    return false;
  };

  return checkValue(data);
};

export default function MultistepForm<T extends FieldValues>({
  stepsData,
  onSubmit,
  title,
  description,
  schemas,
  TitleTag = "h3",
  DescriptionTag = "p",
}: MultistepFormProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Memoize the steps to prevent unnecessary re-renders
  const steps: Step[] = useMemo(
    () => stepsData.filter((data) => data.step).map((data) => data.step!) ?? [],
    [stepsData],
  );

  const methods = useForm<T>({
    resolver: zodResolver(schemas[currentStep]),
    mode: "onSubmit",
    // reValidateMode: "onChange",
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValidating },
    reset,
    setValue,
    watch,
  } = methods;

  // Watch the whole form to detect uploading images
  const formValues = watch();
  const uploadsInProgress = hasUploadsInProgress(formValues);

  const [formData, setFormData] = useState<T>({} as T);

  const NextStep = async (data: Partial<T>) => {
    try {
      // Early return if uploads are in progress
      if (uploadsInProgress) {
        console.log("Cannot proceed while uploads are in progress");
        return;
      }

      setIsSubmitting(true);
      const currentStepData = stepsData[currentStep];

      // Only validate fields that have been touched
      const touchedFields = Object.keys(methods.formState.touchedFields);
      if (touchedFields.length > 0) {
        const isValid = await methods.trigger(touchedFields as any);
        if (!isValid) {
          setIsSubmitting(false);
          return;
        }
      }

      // Update formData with the current step's data
      setFormData((prevData) => ({
        ...prevData,
        ...data, // Merge current step's data with existing data
      }));

      if (currentStepData?.onStepSubmit) {
        await currentStepData.onStepSubmit(data, formData);
      }
      if (currentStepData?.submitStep) {
        await onSubmit({ ...formData, ...data });
      }

      setCurrentStep((prev) => Math.min(prev + 1, stepsData.length - 1));
      setIsSubmitting(false);
    } catch (error) {
      console.log(error);
      setIsSubmitting(false);
    }
  };

  const PrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const StepContent = stepsData[currentStep]?.component;

  const stepUsesCustomNextButton = stepsData[currentStep]?.usesCustomNextButton ?? false;

  const isSuccessStep = stepsData[currentStep]?.isSuccessStep ?? false;

  // Button should be disabled when:
  // 1. Form is submitting OR
  // 2. Validating OR
  // 3. Uploads are in progress
  const isButtonDisabled = isSubmitting || isValidating || uploadsInProgress;

  if (!StepContent) {
    return null;
  }

  return (
    <div className="mx-auto rounded-2xl bg-white p-5 md:max-w-2xl lg:max-w-4xl lg:p-7 xl:max-w-7xl">
      {!isSuccessStep && (
        <>
          <TitleTag className="urbanist text-3xl font-semibold text-primary-foreground">{title}</TitleTag>
          <DescriptionTag className="inter mt-4 text-sm font-normal text-secondary-foreground">
            {description}
          </DescriptionTag>
        </>
      )}

      <FormProvider {...methods}>
        {steps.length > 0 && currentStep !== steps.length - 1 && !isSuccessStep && (
          <StepIndicator steps={steps} currentStep={currentStep} />
        )}

        <StepContent
          stepIndex={currentStep}
          control={control}
          reset={reset}
          setValue={setValue}
          NextStep={handleSubmit(NextStep)}
          PrevStep={PrevStep}
          onSubmit={onSubmit}
          stepsData={stepsData}
          errors={errors}
          formData={formData}
        />
      </FormProvider>

      {currentStep < stepsData.length - 1 && (
        <div className="mt-4 flex justify-center space-x-3 xxxs:justify-end">
          {currentStep > 0 && (
            <Button
              buttonText="Back"
              onClick={PrevStep}
              bgColor="primary-light"
              textColor="primary"
              className="px-9 py-3 text-center transition-all duration-300 ease-in-out hover:bg-primary hover:text-white md:max-w-fit"
              disabled={isSubmitting}
            />
          )}

          {currentStep >= 0 && currentStep < stepsData.length - 2 && !stepUsesCustomNextButton && (
            <div className="relative inline-block">
              <span className="absolute inset-0 animate-pulse-button rounded-xl bg-primary opacity-75"></span>

              <Button
                buttonText={isButtonDisabled ? "Uploading..." : "Next"}
                onClick={handleSubmit(NextStep)}
                bgColor="primary"
                textColor="white"
                className={`relative px-9 py-3 text-center transition-all duration-300 ease-in-out ${
                  isButtonDisabled ? "cursor-not-allowed opacity-70" : "hover:bg-primary-dark"
                } md:max-w-fit`}
                disabled={isButtonDisabled}
                // icon={isButtonDisabled ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : undefined}
              />
            </div>
          )}
          {currentStep === stepsData.length - 2 && (
            <Button
              buttonText={isButtonDisabled ? "Uploading..." : "Submit"}
              onClick={handleSubmit(NextStep)}
              bgColor="primary"
              textColor="white"
              className={`w-full px-9 py-3 text-center md:max-w-fit ${
                isButtonDisabled ? "cursor-not-allowed opacity-70" : ""
              }`}
              disabled={isButtonDisabled}
              // icon={isButtonDisabled ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : undefined}
            />
          )}
        </div>
      )}
    </div>
  );
}
